
import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { Table, Button, Card, CardBody, Alert, Row, Col, UncontrolledTooltip, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, Input } from 'reactstrap';
import { useHistory, useParams } from "react-router-dom";
import { useHealthHubContentsForOrganisation } from "../../hooks/useHealthHubContents";
import { useContentOfTheWeekForOrganisation, ContentOfTheWeekType } from "../../hooks/useContentOfTheWeek";
import { useTaskSequenceBucketForOrganisation } from "../../hooks/useTaskSequencesBuckets";
import { useOrganisationDetail } from "../../hooks/useOrganisation";
import { updateOrganisation } from "../../graphql/content-management/addOrganisations";
import { HealthHubContent } from "../../interfaces/models/HealthHubContent";
import { TaskSequenceBucket } from "../../interfaces/models/TaskSequenceBucket";
import { useEffect, useRef, useState } from "react";
import Loading from "../../components/Loading";
import { getImageSmallUrl } from "../../helper/getImageUrl";
import { ContentOfTheWeek } from "../../interfaces/models/ContentOfTheWeek";
import moment from "moment";
import { DateFormat } from "../../helper/constants";
import { Organisation } from "../../interfaces/models/Organisation";
import toast from "react-hot-toast";
import { useMutation } from "@apollo/client";
import FormError from "../../components/Shared/Forms/FormError";
import ImageUploadInput from "../../components/ContentManagement/ImageUploadInput";
import { Field, Form, Formik, FormikProps } from "formik";
import TextFormInput from "../../components/Shared/Forms/TextFormInput";
import EditOrganisationValidator from "../../validators/EditOrganisationValidator";
import { executeDeleteImage } from "../../graphql/queries/getImageUploadURL";
import useImageUpload from "../../hooks/useImageUpload";

//#region Interface
interface RouteParams {
    id : string;
}

interface ModalProperties {
    action : number;
    modalName : string;
}
//#endregion Interface

const OrganisationDetailsPage = () => {

//#region Initialization
const searchParams = new URLSearchParams(window.location.search);
    const organisationName = searchParams.get("name");
    const { id } = useParams<RouteParams>();

    const modalActions =  {
        DELAY : 1,
        INTERACTION : 2,
        CUSTOMIZE : 3,
        EAP : 4,
    }

    const breadcrumbsData = [
        { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
        { name: 'Organisation List', path: `${DASHBOARD_ROOT}/content-management/organisation-list` },
        { name: organisationName, path: `${DASHBOARD_ROOT}/content-management/organisation-list/${id}` },
    ];

    const maxLength = 100;
    const tableMaxHeight = 300;
    const currentDate = new Date();

    const { data : healthHubContentsData, loading : healthHubContentsLoading, error : healthHubContentsError } = useHealthHubContentsForOrganisation(id);
    const { data : challengeData, loading : challengeLoading, error : challengeError } = useContentOfTheWeekForOrganisation(ContentOfTheWeekType.CHALLENGE, id);
    const { data : bucketData, loading : bucketLoading, error : bucketError } = useTaskSequenceBucketForOrganisation(id);
    const { data : organisationData, loading : organisationLoading, error : organisationError } = useOrganisationDetail(id);

    const [organisation, setOrganisation] = useState<Organisation>(null);

    const [healthHubContents, setHealthHubContents] = useState<HealthHubContent[]>([]);
    const [challenges, setChallenges] = useState<ContentOfTheWeek[]>([]);
    const [bucket, setBucket] = useState<TaskSequenceBucket>(null);

    const [updateOrganisationHook] = useMutation(updateOrganisation,{
        onCompleted : () =>{
            toast.success("Organisation was successfully updated");
        },
        onError : () =>{
            toast.error("Something went wrong whilst Updating the Organisation . Please try again or if issue persists, please contact us");
        }
    });

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modal, setModal] = useState<ModalProperties>({modalName : "", action : modalActions.DELAY});
    const [editableOrganisation, setEditableOrganisation] = useState<Organisation>();
    
    const [isOrgModalOpen, setIsOrgModalOpen] = useState<boolean>(false);

    const formikRef = useRef<FormikProps<Organisation>>(null);
    const [initialValues, setInitialValues] = useState<Organisation>(null);
    const [url, setUrl] = useState<string>("");
    const [fileId, setFileId] = useState<string>("");
    const [blobFile, setBlobFile] = useState<File>(null); 
    const imageInputRef = useRef<{ clearImageField: () => void, refetchUrl : () => void }>(null);

    const { uploadImage } = useImageUpload();
    const [deleteFile] = useMutation(executeDeleteImage);

    const history = useHistory();

    const clearImageField = () => {
        imageInputRef.current?.clearImageField();
    };
    const refetchUrl = () => {
        imageInputRef.current?.refetchUrl();
    };

    const HandleImageChange = (url : string, fileId : string, blob : File) => {
        setUrl(url);
        setFileId(fileId);
        setBlobFile(blob);
    }

    const IsFetchingToggle = (isGetting : boolean) =>{
        setIsProcessing(isGetting);
    }

    const HandleChallengeRedirect = (item : ContentOfTheWeek) => {
        history.push(`${DASHBOARD_ROOT}/content-management/challenge-of-the-week?selected=${item._id}`);
    }

    const HandleHealthHubRedirect = (item : HealthHubContent) => {
        history.push(`${DASHBOARD_ROOT}/content-management/health-hub?selected=${item._id}`);
    }

    const HandleBucketRedirect = (item : TaskSequenceBucket) => {
        history.push(`${DASHBOARD_ROOT}/content-management/day-sequences/buckets?selected=${item._id}`);
    }

    const HandleDelete = async (id : string) => {
        try {
            await deleteFile({
                variables : {
                    fileId : id
                }
            }).catch((err)=>err);
        }
        catch {

        }
    } 

    useEffect(() => {
        if(healthHubContentsData) {
            setHealthHubContents(healthHubContentsData.health_hub_contents_for_org);
        }
    },[healthHubContentsData]);

    useEffect(() => {
        if(challengeData) {
            setChallenges(challengeData.content_of_the_weeks_for_org);
        }
    },[challengeData]);

    useEffect(() => {
        if(bucketData) {
            setBucket(bucketData.task_sequence_bucket_for_org);
        }
    },[bucketData]);

    useEffect(() => {
        if(organisationData) {
            const readOnlyOrg : Organisation = organisationData.organisation;
            setOrganisation(readOnlyOrg);
            setEditableOrganisation(readOnlyOrg);
            if(!readOnlyOrg.eap_link) {
                setEditableOrganisation((prev) => ({...prev, eap_link : "" }))
            }
        }
    },[organisationData]);
//#endregion Initialization

//#region Modal Setup

    const isLinkValid = () => {
        
        const  regex = /((https?):\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/[a-zA-Z0-9#-]*)?(\?[a-zA-Z0-9-_=&]*)?(#?[a-zA-Z0-9]*)?/;
        return editableOrganisation.eap_link ? regex.test(editableOrganisation.eap_link) : true;
    }

    const ToggleEditorModal = async() => setIsModalOpen(!isModalOpen);

    const RenderRequiredInput = () => {
        switch (modal.action) {
            case modalActions.DELAY : 
                return (
                    <FormGroup>
                        <label>Task Sequence Delay</label>
                        <Input type="select" value={editableOrganisation.nudge_delay} 
                        onChange={(e) => setEditableOrganisation((prev) => ({...prev, nudge_delay : Number(e.target.value) }))} >
                            <option value="60">1 Hour</option>
                            <option value="120">2 Hours (Default)</option>
                            <option value="240">4 Hours</option>
                        </Input>
                    </FormGroup> 
                )
            case modalActions.INTERACTION : 
                return (
                    <FormGroup>
                        <label>Nudge Interaction</label>
                        <Input type="select" value={editableOrganisation.nudge_interaction.toString().toLowerCase()} 
                        onChange={(e) => setEditableOrganisation((prev) => ({...prev, nudge_interaction : e.target.value === "true" }))} >
                            <option value="true">ON (Default)</option>
                            <option value="false">OFF</option>
                        </Input>
                    </FormGroup>   
                )
            case modalActions.CUSTOMIZE : 
                return (
                    <FormGroup>
                        <label>Allow Organisation Users Customise</label>
                        <Input type="select" value={editableOrganisation.allow_user_customisation.toString().toLowerCase()} 
                        onChange={(e) => setEditableOrganisation((prev) => ({...prev, allow_user_customisation : e.target.value === "true" }))} >
                            <option value="false">NO (Default)</option>
                            <option value="true">YES</option>
                        </Input>
                    </FormGroup>
                )
            case modalActions.EAP : 
                return (
                    <FormGroup>
                        <label>EAP Link</label>
                        <div className="d-flex">
                            <Input type="text" value={editableOrganisation.eap_link} 
                            placeholder="Link from the organisation, leave blank if none"
                            onChange={(e) => setEditableOrganisation((prev) => ({...prev, eap_link : e.target.value }))} />
                            <Button color="secondary" className="ml-2" onClick={() => setEditableOrganisation((prev) => ({...prev, eap_link : "" }))}>Clear</Button>
                        </div>
                        {
                            !isLinkValid() && 
                            <FormError>Please provide a valid url</FormError>
                        }
                        
                    </FormGroup>
                )
            default :
                return <></>

        }

    }
    
    const TriggerSubmit = async () => {
        
        if(modal.action === modalActions.EAP && !isLinkValid()) {
            return;
        }
        setIsProcessing(true);

        let isUpdated = await updateOrganisationHook({
            variables : {
                _id : id,
                record : {
                    nudge_delay : editableOrganisation.nudge_delay,
                    nudge_interaction : editableOrganisation.nudge_interaction,
                    nudge_no_interaction_start : editableOrganisation.nudge_interaction === false ? moment().add(1,"days").format("YYYY-MM-DD") : null,
                    allow_user_customisation : editableOrganisation.allow_user_customisation,
                    eap_link : editableOrganisation.eap_link,
                }
            }
        }).then((res) => res.errors ? false : true).catch(()=> false);
        setIsProcessing(false);

        if(isUpdated) {
            setOrganisation(editableOrganisation);
            ToggleEditorModal();
        }
    }


//#endregion Modal Setup
//#region Update Nudge Delay
    const OpenNudgeModal = () => {
        setModal({
            modalName : "Nudge Delay Settings",
            action : modalActions.DELAY,
        });
        setEditableOrganisation(organisation);
        ToggleEditorModal();
    }
//#endregion Update Nudge Delay
//#region Update Nudge Interaction
    const OpenNudgeButtonModal = () => {
        setModal({
            modalName : "Nudge Interaction Settings",
            action : modalActions.INTERACTION,
        });
        setEditableOrganisation(organisation);
        ToggleEditorModal();
    }
//#endregion Update Nudge Interaction
//#region User Customisation 
    const OpenUserCustomisationModal = () => {
        
        setModal({
            modalName : "Nudge Customisation",
            action : modalActions.CUSTOMIZE,
        });
        setEditableOrganisation(organisation);
        ToggleEditorModal();
    }
//#endregion User Customisation 
//#region EAP Functions
//#region Organisation Info Edit

const OpenOrgModal = () => {
    setInitialValues(organisation);
    clearImageField();
    ToggleOrgModal();
}

const ToggleOrgModal = () => setIsOrgModalOpen(!isOrgModalOpen);

const SubmitOrgUpdate  = async (formData : Organisation) => {

    if(formData.image_name && !blobFile) 
    {
        return;
    }
    setIsProcessing(true);

    //if there a new file
    if(blobFile){
        // proceed to upload
        const isUploaded : boolean = await uploadImage(url,blobFile);
        if(!isUploaded) {
            toast.error("Something went wrong whilst Uploading image. Please try again or if issue persists, please contact us");
            setIsProcessing(false);
            return;
        }
    }

    let isUpdated = await updateOrganisationHook({
        variables : {
            _id : id,
            record : {
                name : formData.name,
                description : formData.description,
                image_id : fileId || null,
                image_name : fileId ? formData.image_name : null,
            }
        }
    }).then((res) => res.errors ? false : true).catch(()=> false);

    if(!isUpdated) {
        if(fileId) {
            refetchUrl();
            // Delete the file if it was uploaded
            await HandleDelete(fileId);
        }
        setIsProcessing(false);
        return;  
    }

    // Delete old image id if needed
    if((organisation.image_id && fileId) || (fileId == null && organisation.image_id)) {
        await HandleDelete(organisation.image_id);
    }
    const imageName = fileId ? formData.image_name : null;
    setOrganisation((prev) => (
    { ...prev, 
            name : formData.name, 
            description : formData.description, 
            image_id : fileId || organisation.image_id, 
            image_name : imageName
    }));
    setTimeout(() => {
        window.location.href = `${DASHBOARD_ROOT}/content-management/organisation-list/${id}?name=${formData.name}`;
    },700)
}
//#endregion Organisation Info Edit
const OpenEapModal = () => {
    setModal({
        modalName : "Nudge EAP Link",
        action : modalActions.EAP,
    });
    setEditableOrganisation(organisation);
    ToggleEditorModal();
}
//#endregion EAP Functions
    return (
        <>
            <Breadcrumbs 
                crumbs={breadcrumbsData}
            />
            {
                organisationError &&
                <Alert color="danger">
                    Error on Getting Organisation Details
                </Alert>
            }
            <Card>
                <CardBody>
                    {
                        organisationLoading && 
                        <Loading />
                    }
                    {
                        organisationData && 
                        <Row>
                            <Col md={4} className="d-flex align-items-center justify-content-center">
                                {
                                    organisation?.image_id ? 
                                    <img
                                        style={{
                                            height : "100px",
                                            width : "100%",
                                            objectFit :"contain"
                                        }}
                                        src={getImageSmallUrl(organisation.image_id)}
                                        alt={organisation.name}
                                    />
                                    :
                                    <span className="text-muted"> No Logo</span>
                                }
                            </Col>
                            <Col md={8} className="d-flex align-items-center justify-content-between">
                                <div>
                                    <div><h4 style={{ marginBottom: "1rem" }}>{organisation?.name}</h4></div>
                                    <div>
                                        <p className="text-xs" style={{ wordWrap :"break-word",whiteSpace: "pre-wrap"}}>{organisation?.description}</p>
                                    </div>
                                </div>
                                <div>
                                    <Button color="primary"  onClick={OpenOrgModal}>Edit</Button>
                                </div>
                            </Col>
                        </Row>
                    }
                </CardBody>
            </Card>
            <h3 className="pt-2">Nudge Settings</h3>
            <Row>
                <Col>
                    <Card>
                        <CardBody className="text-center">
                            <h4>Nudge Delay</h4>
                            
                            {
                                organisationLoading ? 
                                    <Loading />
                                :
                                    <div>
                                        <UncontrolledTooltip
                                            placement="bottom"
                                            target="taskSequenceDelaySpan"
                                            
                                        >
                                            Click to Edit
                                        </UncontrolledTooltip>
                                        <span className="text-primary cursor-pointer " id="taskSequenceDelaySpan" onClick={OpenNudgeModal}>
                                            <i className="far fa-edit"></i> {organisation?.nudge_delay ? organisation.nudge_delay / 60 : 2} Hour(s)
                                        </span>
                                    </div>
                            }
                            
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card>
                        <CardBody className="text-center">
                            <h4>Nudge Interaction</h4>
                            {
                                organisationLoading ? 
                                    <Loading />
                                :
                                    <div>
                                        <UncontrolledTooltip
                                            placement="bottom"
                                            target="buttonsForNudgesSpan"
                                        >
                                            Click to Edit
                                        </UncontrolledTooltip>
                                        <span className="text-primary cursor-pointer" id="buttonsForNudgesSpan" onClick={OpenNudgeButtonModal}>
                                            <i className="far fa-edit"></i> {organisation?.nudge_interaction === false ? "OFF" : "ON"}
                                        </span>
                                    </div>
                            }
                            
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card>
                        <CardBody className="text-center">
                        <h4>Nudge Customisation</h4>
                            {
                                organisationLoading ? 
                                    <Loading />
                                :
                                    <div>
                                        <UncontrolledTooltip
                                            placement="bottom"
                                            target="buttonsForCustomisationSpan"
                                        >
                                            Click to Edit
                                        </UncontrolledTooltip>
                                        <span className="text-primary cursor-pointer" id="buttonsForCustomisationSpan" onClick={OpenUserCustomisationModal}>
                                            <i className="far fa-edit"></i> {organisation?.allow_user_customisation === false ? "NO" : "YES"}
                                        </span>
                                    </div>
                            }
                            
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card>
                        <CardBody className="text-center">
                        <h4>Nudge EAP</h4>
                            {
                                organisationLoading ? 
                                    <Loading />
                                :
                                    <div>
                                        <UncontrolledTooltip
                                            placement="bottom"
                                            target="buttonsForEapSpan"
                                        >
                                            Click to Edit
                                        </UncontrolledTooltip>
                                        <span className="text-primary cursor-pointer" id="buttonsForEapSpan" onClick={OpenEapModal}>
                                            <i className="far fa-edit"></i>  {organisation?.eap_link ? "YES" : "NO"}
                                        </span>
                                    </div>
                            }
                            
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <h3 className="pt-2">Challenge of the Week Contents</h3>
            <Card>
                <CardBody>
                    {
                        challengeError &&
                        <Alert color="danger">
                            Error on Getting Challenges
                        </Alert>
                    }
                    
                    <div className="table-responsive" style={{maxHeight : tableMaxHeight}}>
                        <Table striped bordered>
                             <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Title & Description</th>
                                    <th>Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                            {
                                challengeLoading && 
                                <tr>
                                    <td colSpan={4}>
                                        <Loading />
                                    </td>
                                </tr>
                            }
                            {
                                challenges.length === 0 && !challengeLoading &&
                                <tr>
                                    <td colSpan={4} className="text-center">The default Challenge will be shown to this organisation.</td>
                                </tr>
                            }
                            {
                                challenges.map((item, key) => {
                                    return (
                                        <tr key={key}>
                                            <td>
                                                <img
                                                    style={{
                                                        borderRadius: "0.375rem",
                                                        marginBottom: "1rem",
                                                        height : "100px"
                                                    }}
                                                    src={getImageSmallUrl(item.image_id)}
                                                    alt={item.title}
                                                />
                                            </td>
                                            <td>
                                                <div className="font-weight-bold">{item.title}</div>
                                                <div className="table-description-details">
                                                    { item.description.length > maxLength ? 
                                                    item.description.slice(0, maxLength) + " ..."
                                                    : item.description }
                                                </div>
                                            </td>
                                            <td>
                                                {
                                                    new Date(item.end_date) < currentDate 
                                                    ? <span title="Date already passed" className=" text-danger"><i className="far fa-calendar-times fa-lg"></i> {moment(item.start_date).format(DateFormat)} to {moment(item.end_date).format(DateFormat)}</span>
                                                    : new Date(item.start_date) <= currentDate && new Date(item.end_date) >= currentDate 
                                                    ? <span title="Currently Showing for the week" className=" text-success"><i className="far fa-calendar-check fa-lg"></i> {moment(item.start_date).format(DateFormat)} to {moment(item.end_date).format(DateFormat)}</span>
                                                    : <span title="Set for the Future Dates" className=" text-primary"><i className="fas fa-hourglass-half"></i> {moment(item.start_date).format(DateFormat)} to {moment(item.end_date).format(DateFormat)}</span>
                                                } 
                                            </td>
                                            <td>
                                                <Button color="primary" size='sm' onClick={()=> { HandleChallengeRedirect(item) }} >View</Button>
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>
            <h3 className="pt-2">Health Hub Contents</h3>
            <Card>
                <CardBody>
                    {
                        healthHubContentsError &&
                        <Alert color="danger">
                            Error on Getting Health Hub Contents
                        </Alert>
                    }
                    <div className="table-responsive" style={{maxHeight : tableMaxHeight}}>
                        <Table striped bordered>
                            <colgroup>
                                <col width="30%"></col>
                                <col width="30%"></col>
                                <col width="30%"></col>
                                <col width="10%"></col>
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Title & Description</th>
                                    <th>Link</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                            {
                                healthHubContentsLoading && 
                                <tr>
                                    <td colSpan={4}>
                                        <Loading />
                                    </td>
                                </tr>
                            }
                            {
                                healthHubContents.length === 0 && !healthHubContentsLoading &&
                                <tr>
                                    <td colSpan={4} className="text-center">The default Health Hub Links will be shown to this organization.</td>
                                </tr>
                            }
                            {
                                healthHubContents.map((link, key) => {
                                    return (
                                        <tr key={key}>
                                            <td>
                                                <img
                                                    style={{
                                                        borderRadius: "0.375rem",
                                                        marginBottom: "1rem",
                                                        height : "100px"
                                                    }}
                                                    src={getImageSmallUrl(link.image_id)}
                                                    alt={link.alt}
                                                />
                                            </td>
                                            <td>
                                                <div className="font-weight-bold">{link.name}</div>
                                                <div className="table-description-details">
                                                    { link.description.length > maxLength ? 
                                                    link.description.slice(0, maxLength) + " ..."
                                                    : link.description }
                                                </div>
                                            </td>
                                            <td >
                                                <div className="table-description-details" >
                                                    <a href={link.link} target="_blank" rel="noreferrer">
                                                        {link.link}
                                                    </a>
                                                </div>
                                            </td>
                                            <td>
                                                <Button color="primary" size='sm' onClick={()=> { HandleHealthHubRedirect(link) }} >View</Button>
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>
            <h3 className="pt-2">Sequence Bucket</h3>
            <Card>
                <CardBody>
                    {
                        bucketError &&
                        <Alert color="danger">
                            Error on Getting Bucket for Organisation
                        </Alert>
                    }
                    <div className="table-responsive" style={{maxHeight : tableMaxHeight}}>
                        <Table striped bordered>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Type</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    bucketLoading && 
                                    <tr>
                                        <td colSpan={4}>
                                            <Loading />
                                        </td>
                                    </tr>
                                }
                                {
                                    !bucket && !bucketLoading &&
                                    <tr>
                                        <td colSpan={4} className="text-center">The default Task Sequence Bucket will be shown to this organization.</td>
                                    </tr>
                                }
                                {
                                    bucket && 
                                    <tr >
                                        <td>{bucket.name}</td>
                                        <td>
                                            <div className="table-description-details">
                                                    { bucket.description.length > maxLength ? 
                                                    bucket.description.slice(0, maxLength) + " ..."
                                                    : bucket.description }
                                                </div>
                                        </td>
                                        <td>{bucket.type}-Day Sequence</td>
                                        <td>
                                            <Button color="primary" size='sm' onClick={()=> { HandleBucketRedirect(bucket) }}>View</Button>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>

            <Modal
                id="nudge-editor-modal"
                backdrop={true}
                isOpen={isModalOpen}>
                <ModalHeader toggle={ToggleEditorModal} className="pb-0">
                    <p className="text-lg">{modal.modalName}</p>
                </ModalHeader>
                <ModalBody>
                    {
                        editableOrganisation &&
                        RenderRequiredInput()
                    }              
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" disabled={isProcessing} onClick={ToggleEditorModal}>Cancel</Button>
                    <Button color="primary" disabled={isProcessing} onClick={TriggerSubmit} >Save</Button>
                </ModalFooter>
            </Modal>
            <Modal
                id="org-editor-modal"
                backdrop={true}
                isOpen={isOrgModalOpen}>
                <ModalHeader toggle={ToggleOrgModal} className="pb-0">
                    <p className="text-lg">Edit Organisation</p>
                </ModalHeader>
                <ModalBody>
                    <Formik
                        innerRef={formikRef}
                        initialValues={initialValues}
                        onSubmit={async (values : Organisation) => {
                            
                            await SubmitOrgUpdate(values);
                        }}
                        validationSchema={EditOrganisationValidator}
                    >
                        {({errors, touched}) => (
                            <Form>
                                <TextFormInput
                                    name={"name"}
                                    placeholder="Enter Name"
                                    label="Name"
                                    required={true}
                                />
                                <div className="form-group mb-0">
                                    <label>Description *</label>
                                    <Field
                                        className="form-control"
                                        name="description"
                                        placeholder="Enter description"
                                        rows={8}
                                        as="textarea"
                                    />
                                    <FormError>{touched.description && (errors.description)}</FormError>
                                </div>
                                <ImageUploadInput
                                    ref={imageInputRef}
                                    formikRef={formikRef}
                                    transferFunction={HandleImageChange}
                                    isGettingUrl={IsFetchingToggle}
                                    label="Logo"
                                    imageName="image_name"
                                    imageIdName="image_id"
                                    extensionAllowed="image/jpeg,image/jpg,image/png"
                                    isDisabled={isProcessing}
                                />
                            </Form>
                        )}
                        
                    </Formik>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" disabled={isProcessing} onClick={ToggleOrgModal}>Cancel</Button>
                    <Button color="primary" disabled={isProcessing} onClick={() => formikRef?.current.submitForm() } >Save</Button>
                </ModalFooter>
            </Modal>
        </>
    )

}
export { OrganisationDetailsPage as OrganisationDetails };